import { __decorate } from "tslib";
import React, { Children, Component } from 'react';
import classNames from 'classnames';
import omit from 'lodash/omit';
import noop from 'lodash/noop';
import { Size } from '../_util/enum';
import Grid from './Grid';
import Meta from './Meta';
import Tabs from '../tabs';
import { throttleByAnimationFrameDecorator } from '../_util/throttleByAnimationFrame';
import warning from '../_util/warning';
import addEventListener from '../_util/addEventListener';
import ConfigContext from '../config-provider/ConfigContext';
export { CardGridProps } from './Grid';
export { CardMetaProps } from './Meta';
export default class Card extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            widerPadding: false,
            size: 'xl',
        };
        this.onTabChange = (key) => {
            const { onTabChange } = this.props;
            if (onTabChange) {
                onTabChange(key);
            }
        };
        this.saveRef = (node) => {
            this.container = node;
        };
    }
    static get contextType() {
        return ConfigContext;
    }
    componentDidMount() {
        this.updateWiderPadding();
        this.updateWiderSize();
        this.resizeEvent = addEventListener(window, 'resize', this.updateWiderPadding);
        if ('noHovering' in this.props) {
            const { noHovering } = this.props;
            warning(!noHovering, '`noHovering` of Card is deperated, you can remove it safely or use `hoverable` instead.');
            warning(!!noHovering, '`noHovering={false}` of Card is deperated, use `hoverable` instead.');
        }
    }
    componentWillUnmount() {
        if (this.resizeEvent) {
            this.resizeEvent.remove();
        }
        this.updateWiderPadding.cancel();
    }
    updateWiderPadding() {
        if (!this.container) {
            return;
        }
        // 936 is a magic card width pixel number indicated by designer
        const WIDTH_BOUNDARY_PX = 936;
        const { widerPadding } = this.state;
        if (this.container.offsetWidth >= WIDTH_BOUNDARY_PX && !widerPadding) {
            this.setState({ widerPadding: true }, () => {
                this.updateWiderPaddingCalled = true; // first render without css transition
            });
        }
        if (this.container.offsetWidth < WIDTH_BOUNDARY_PX && widerPadding) {
            this.setState({ widerPadding: false }, () => {
                this.updateWiderPaddingCalled = true; // first render without css transition
            });
        }
    }
    updateWiderSize() {
        if (this.container) {
            if (this.container.offsetHeight <= 50) {
                this.setState({
                    size: 'xs',
                });
            }
        }
    }
    isContainGrid() {
        const { children } = this.props;
        return Children.toArray(children).some((element) => element && element.type && element.type === Grid);
    }
    getAction(actions) {
        if (!actions || !actions.length) {
            return null;
        }
        const actionList = actions.map((action, index) => (React.createElement("li", { style: { width: `${100 / actions.length}%` }, key: `action-${String(index)}` },
            React.createElement("span", null, action))));
        return actionList;
    }
    // For 2.x compatible
    getCompatibleHoverable() {
        const { noHovering, hoverable } = this.props;
        if ('noHovering' in this.props) {
            return !noHovering || hoverable;
        }
        return !!hoverable;
    }
    render() {
        const { prefixCls: customizePrefixCls, className, extra, bodyStyle, title, loading, bordered = true, type, cover, actions, tabList, children, activeTabKey, defaultActiveTabKey, onHeadClick, tabsProps, selected, cornerPlacement, onSelectChange = noop, ...others } = this.props;
        const { widerPadding, size } = this.state;
        const { getPrefixCls } = this.context;
        const prefixCls = getPrefixCls('card', customizePrefixCls);
        const selectedPrefixCls = `${prefixCls}-selected`;
        const classString = classNames(prefixCls, className, {
            [`${prefixCls}-loading`]: loading,
            [`${prefixCls}-bordered`]: bordered,
            [`${prefixCls}-hoverable`]: this.getCompatibleHoverable(),
            [`${prefixCls}-wider-padding`]: widerPadding,
            [`${prefixCls}-padding-transition`]: this.updateWiderPaddingCalled,
            [`${prefixCls}-contain-grid`]: this.isContainGrid(),
            [`${prefixCls}-contain-tabs`]: tabList && tabList.length,
            [`${prefixCls}-type-${type}`]: !!type,
            [`${selectedPrefixCls} ${selectedPrefixCls}-${cornerPlacement} ${selectedPrefixCls}-${size}`]: selected,
            [`${prefixCls}-only-title`]: !children,
            [`${prefixCls}-only-body`]: !title,
        });
        const loadingBlock = (React.createElement("div", { className: `${prefixCls}-loading-content` },
            React.createElement("p", { className: `${prefixCls}-loading-block`, style: { width: '94%' } }),
            React.createElement("p", null,
                React.createElement("span", { className: `${prefixCls}-loading-block`, style: { width: '28%' } }),
                React.createElement("span", { className: `${prefixCls}-loading-block`, style: { width: '62%' } })),
            React.createElement("p", null,
                React.createElement("span", { className: `${prefixCls}-loading-block`, style: { width: '22%' } }),
                React.createElement("span", { className: `${prefixCls}-loading-block`, style: { width: '66%' } })),
            React.createElement("p", null,
                React.createElement("span", { className: `${prefixCls}-loading-block`, style: { width: '56%' } }),
                React.createElement("span", { className: `${prefixCls}-loading-block`, style: { width: '39%' } })),
            React.createElement("p", null,
                React.createElement("span", { className: `${prefixCls}-loading-block`, style: { width: '21%' } }),
                React.createElement("span", { className: `${prefixCls}-loading-block`, style: { width: '15%' } }),
                React.createElement("span", { className: `${prefixCls}-loading-block`, style: { width: '40%' } }))));
        const hasActiveTabKey = activeTabKey !== undefined;
        const extraProps = {
            [hasActiveTabKey ? 'activeKey' : 'defaultActiveKey']: hasActiveTabKey
                ? activeTabKey
                : defaultActiveTabKey,
        };
        let head;
        const tabs = tabList && tabList.length ? (React.createElement(Tabs, Object.assign({}, tabsProps, extraProps, { className: `${prefixCls}-head-tabs`, size: Size.large, onChange: this.onTabChange }), tabList.map(item => (React.createElement(Tabs.TabPane, { tab: item.tab, disabled: item.disabled, key: item.key }))))) : null;
        if (title || extra || tabs) {
            head = (React.createElement("div", { className: `${prefixCls}-head`, onClick: onHeadClick },
                React.createElement("div", { className: `${prefixCls}-head-wrapper` },
                    title && React.createElement("div", { className: `${prefixCls}-head-title` }, title),
                    extra && React.createElement("div", { className: `${prefixCls}-extra` }, extra)),
                tabs));
        }
        const coverDom = cover ? React.createElement("div", { className: `${prefixCls}-cover` }, cover) : null;
        const body = (React.createElement("div", { className: `${prefixCls}-body`, style: { ...bodyStyle, cursor: typeof onSelectChange === 'function' ? 'pointer' : 'none' }, onClick: () => onSelectChange(!selected) }, loading ? loadingBlock : children));
        const actionDom = actions && actions.length ? (React.createElement("ul", { className: `${prefixCls}-actions` }, this.getAction(actions))) : null;
        const divProps = omit(others, ['onTabChange', 'noHovering', 'hoverable']);
        return (React.createElement("div", Object.assign({}, divProps, { className: classString, ref: this.saveRef }),
            head,
            coverDom,
            body,
            actionDom));
    }
}
Card.displayName = 'Card';
Card.Grid = Grid;
Card.Meta = Meta;
Card.defaultProps = {
    cornerPlacement: 'bottomRight',
};
__decorate([
    throttleByAnimationFrameDecorator()
], Card.prototype, "updateWiderPadding", null);
